import { useState } from "react";
import { FaTrash } from "react-icons/fa";
import Button from "Components/UI/Button";
import DetailSection from "Components/PatentDesign/DetailSection";
import FileList from "Components/PatentDesign/FileList";
import { disabledStyle, pdStyles } from "Components/PatentDesign/styles";
import {
  createPatentDesignNotice,
  deletePatentDesignNotice,
  downloadPatentDesignDocument,
} from "JS/API";
import { designTokens } from "utils/designSystem";
import usePatentDesignDownload from "utils/usePatentDesignDownload";
import {
  FILE_ACCEPT,
  NOTICE_MAX_BYTES,
  ERROR_MESSAGE,
  formatDateTime,
  formatFileSize,
  normalizeNewlines,
  utf8ByteLength,
  validateUploadFiles,
} from "utils/patentDesign";

const toFileItems = (files = []) =>
  files.map((file) => ({ id: file.id, name: file.original_file_name, size: file.file_size }));

const NoticeSection = ({ detail, onReload, onAlert, onError }) => {
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { isDownloading, download } = usePatentDesignDownload({ onAlert, onError });

  const notices = detail.notices || [];
  const bytes = utf8ByteLength(normalizeNewlines(content));

  const handleFilesChange = (e) => {
    const added = Array.from(e.target.files || []);
    e.target.value = "";
    if (!added.length) return;
    const next = [...files, ...added];
    const error = validateUploadFiles(next);
    if (error) {
      onAlert(ERROR_MESSAGE[error]);
      return;
    }
    setFiles(next);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const normalized = normalizeNewlines(content);
    if (!normalized.trim()) {
      onAlert("안내 내용을 입력해 주세요.");
      return;
    }
    if (utf8ByteLength(normalized) > NOTICE_MAX_BYTES) {
      onAlert(`안내 내용은 ${NOTICE_MAX_BYTES}byte 이하로 입력해 주세요.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createPatentDesignNotice(detail.id, { content: normalized, files });
      if (result && typeof result === "object") {
        setContent("");
        setFiles([]);
        onAlert("고객 안내를 등록했습니다.");
        onReload();
      } else if (ERROR_MESSAGE[result]) {
        onAlert(ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (notice) => {
    if (deletingId) return;
    if (!window.confirm("이 안내를 삭제할까요? 첨부 파일도 함께 삭제됩니다.")) return;
    setDeletingId(notice.id);
    try {
      const result = await deletePatentDesignNotice(notice.id);
      if (result && typeof result === "object") {
        onAlert("안내를 삭제했습니다.");
        onReload();
      } else if (ERROR_MESSAGE[result]) {
        onAlert(ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DetailSection title="고객 안내">
      <div style={pdStyles.form}>
        <div>
          <label htmlFor="patent-design-notice-content" style={pdStyles.label}>
            안내 내용
          </label>
          <textarea
            id="patent-design-notice-content"
            style={pdStyles.textarea}
            placeholder="고객 마이페이지에 보일 안내를 입력해 주세요."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <p
            style={{
              ...pdStyles.hint,
              textAlign: "right",
              color: bytes > NOTICE_MAX_BYTES ? designTokens.colors.danger : undefined,
            }}
          >
            {bytes} / {NOTICE_MAX_BYTES} byte
          </p>
        </div>
        <div>
          <label htmlFor="patent-design-notice-files" style={pdStyles.label}>
            안내 첨부 파일
          </label>
          <input
            id="patent-design-notice-files"
            type="file"
            multiple
            accept={FILE_ACCEPT}
            onChange={handleFilesChange}
          />
          <p style={{ ...pdStyles.hint, marginTop: designTokens.spacing.xs }}>
            jpg · png · pdf · hwp · doc · ppt · zip, 최대 5개 · 개당 10MB · 합계 40MB
          </p>
          {files.length > 0 && (
            <ul style={{ ...pdStyles.fileList, marginTop: designTokens.spacing.sm }}>
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`} style={pdStyles.fileItem}>
                  <span style={pdStyles.fileName}>{file.name}</span>
                  <span style={pdStyles.fileMeta}>{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    aria-label={`${file.name} 빼기`}
                    style={pdStyles.dangerLink}
                    onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                  >
                    빼기
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={disabledStyle(isSubmitting)}
          >
            안내 등록
          </Button>
        </div>
      </div>

      {notices.length ? (
        <ul style={pdStyles.list}>
          {notices.map((notice) => {
            const createdAt = formatDateTime(notice.created_at);
            return (
              <li key={notice.id} style={pdStyles.card}>
                <div style={pdStyles.cardHeader}>
                  <span style={pdStyles.muted}>{createdAt}</span>
                  <button
                    type="button"
                    aria-label={`${createdAt} 안내 삭제`}
                    style={{ ...pdStyles.dangerLink, ...disabledStyle(deletingId === notice.id) }}
                    disabled={deletingId === notice.id}
                    onClick={() => handleDelete(notice)}
                  >
                    <FaTrash size={11} />
                    삭제
                  </button>
                </div>
                <p style={pdStyles.content}>{notice.content}</p>
                {notice.files?.length > 0 && (
                  <FileList
                    files={toFileItems(notice.files)}
                    disabled={isDownloading}
                    onDownload={(file) => download([file], downloadPatentDesignDocument)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p style={pdStyles.muted}>등록된 안내가 없어요</p>
      )}
    </DetailSection>
  );
};

export default NoticeSection;
